import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { Option } from '@sapphire/result';
import { map, tap } from 'rxjs';

import { ImpersonationActions } from '@ps/auth-data-access';
import { APP_ROUTES } from '@ps/core-config';
import { EffectsBase } from '@ps/core-data-access';
import { PlatformSwitcherService } from '@ps/platform-switcher-data-access';
import {
  isSpecificAudienceChannel,
  Survey,
  trafficChannelParentId,
} from '@ps/survey-utils';
import { Nullable } from '@ps/utils';

import {
  SurveyCreationCoreActions,
  SurveyCreationCoreApiActions,
} from './survey-creation-core.actions';
import { selectSurvey } from './survey-creation-core.selectors';

// Reacts to a confirmed exit from the survey workflow — discard, save-and-exit,
// or save-and-exit-with-impersonation-change. Each effect owns exactly one
// outcome, split out of SurveyCreationSaveEffects so that class never has to
// know why a caller wanted to exit, only how to save.
@Injectable()
export class SurveyCreationWorkflowExitEffects extends EffectsBase {
  private readonly _actions$ = inject(Actions);
  private readonly _router = inject(Router);
  private readonly _platformSwitcher = inject(PlatformSwitcherService);
  private readonly _store = inject(Store);

  public readonly discardSurveyChanges$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(SurveyCreationCoreActions.discardSurveyChanges),
        concatLatestFrom(() => this._store.select(selectSurvey)),
        tap(([, survey]) => this._router.navigate([this._exitRoute(survey)])),
      ),
    { dispatch: false },
  );

  public readonly disconnectImpersonationOnSaveSuccess$ = createEffect(() =>
    this._actions$.pipe(
      ofType(
        SurveyCreationCoreApiActions.saveSurveyAndDisconnectImpersonationSuccess,
      ),
      map(() => ImpersonationActions.disconnect()),
    ),
  );

  public readonly switchImpersonatedBuyerOnSaveSuccess$ = createEffect(() =>
    this._actions$.pipe(
      ofType(
        SurveyCreationCoreApiActions.saveSurveyAndSwitchImpersonatedBuyerSuccess,
      ),
      map(({ buyer }) => ImpersonationActions.switchBuyer(buyer)),
    ),
  );

  public readonly switchToLegacyOnSaveSuccess$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(SurveyCreationCoreApiActions.saveSurveyAndSwitchToLegacySuccess),
        tap(({ routeKey, params }) =>
          this._platformSwitcher
            .switchToLegacy({ routeKey, params, force: true })
            .subscribe(),
        ),
      ),
    { dispatch: false },
  );

  public readonly navigateOnSaveAndExitSuccess$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(SurveyCreationCoreApiActions.saveSurveyAndExitSuccess),
        tap(({ survey }) => this._router.navigate([this._exitRoute(survey)])),
      ),
    { dispatch: false },
  );

  // Changing who is signed in leaves the survey behind, so these always go to
  // the dashboard.
  public readonly navigateToDashboardOnSurveyExitSuccess$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(
          SurveyCreationCoreApiActions.saveSurveyAndDisconnectImpersonationSuccess,
          SurveyCreationCoreApiActions.saveSurveyAndSwitchImpersonatedBuyerSuccess,
        ),
        tap(() => this._router.navigate([APP_ROUTES.DASHBOARD])),
      ),
    { dispatch: false },
  );

  // A Specific Audience channel is edited from its parent's Manage Survey
  // page, so leaving it returns there; any other survey exits to the dashboard.
  private _exitRoute(survey: Nullable<Survey>): string {
    return Option.from(survey)
      .filter(isSpecificAudienceChannel)
      .andThen(trafficChannelParentId)
      .mapOr(APP_ROUTES.DASHBOARD, APP_ROUTES.manageSurvey);
  }
}
